/**
 * Converts between this app's `NiftiVolume` (`lib/nifti.ts`) and itk-wasm's
 * own `Image` interface, so the real `@itk-wasm/*` pipelines (downsample,
 * compare-images, morphological-contour-interpolation, dicom) can run
 * directly against volumes this app already has in memory, with no
 * intermediate file I/O.
 *
 * Verified against the real Node pipelines in `scripts/verify-itk-wasm-tools.mjs`
 * (`downsampleNode`/`compareImagesNode`/`morphologicalContourInterpolationNode`
 * against synthetic data) -- a shape and dtype mismatch here would silently
 * misalign output volumes the same way the RAS/LPI orientation bugs earlier
 * in this project's history did, so this is not guessed.
 *
 * Layout: both this app's `voxels`/`data` arrays and itk-wasm's flat buffer
 * use the same "fastest-varying-first" order (`idx = x + y*nx + z*nx*ny`),
 * confirmed in `get-matrix-element.js`'s `column + row*columns` convention
 * and itk-wasm's own buffer layout -- no transpose needed.
 *
 * Direction matrix: itk-wasm's `direction` is a flat D*D array, row-major
 * (`matrixData[column + row*columns]`, from itk-wasm's `get-matrix-element.js`).
 * ITK's own convention is that each *column* of the direction matrix is the
 * physical-space unit direction cosine of the corresponding image axis, and
 * `affine3x3[:, i] = direction[:, i] * spacing[i]`. So:
 *   spacing[i]        = ||affine3x3 column i||
 *   direction[:, i]    = affine3x3 column i / spacing[i]
 *   origin             = affine translation column
 */
import { FloatTypes, Image, ImageType, IntTypes, PixelTypes } from "itk-wasm";

import type { NiftiVolume } from "@/lib/nifti";

function affineElement(affine: Float32Array, row: number, col: number): number {
  // NiftiVolume.affine is row-major 4x4: element (row, col) at row*4+col.
  return affine[row * 4 + col];
}

function geometry(vol: NiftiVolume) {
  const spacing = [0, 0, 0];
  const direction = new Float64Array(9);
  for (let col = 0; col < 3; col++) {
    const colVec = [affineElement(vol.affine, 0, col), affineElement(vol.affine, 1, col), affineElement(vol.affine, 2, col)];
    const len = Math.hypot(colVec[0], colVec[1], colVec[2]) || 1;
    spacing[col] = len;
    for (let row = 0; row < 3; row++) direction[row * 3 + col] = colVec[row] / len;
  }
  const origin = [affineElement(vol.affine, 0, 3), affineElement(vol.affine, 1, 3), affineElement(vol.affine, 2, 3)];
  return { spacing, direction, origin };
}

export function niftiVolumeToItkImage(vol: NiftiVolume, name = "image"): Image {
  const [nx, ny, nz] = vol.dims;
  const { spacing, direction, origin } = geometry(vol);

  const img = new Image(new ImageType(3, FloatTypes.Float32, PixelTypes.Scalar, 1));
  img.name = name;
  img.origin = origin;
  img.spacing = spacing;
  img.direction = direction;
  img.size = [nx, ny, nz];
  img.data = vol.voxels;
  return img;
}

/**
 * Same as `niftiVolumeToItkImage`, but as a UInt8 label image instead of
 * Float32 scalar -- required by pipelines that dispatch on pixel type, e.g.
 * `@itk-wasm/morphological-contour-interpolation`'s wasm CLI throws
 * "Unsupported image type" on a float32 input (confirmed live: it only
 * accepts an integer component type). This app's own `NiftiVolume`
 * convention stores every volume, including masks, as Float32 (see
 * `itkImageToNiftiVolume`'s comment and `lib/export.ts`'s `maskToVol`), so
 * callers that need a label image must go through this conversion rather
 * than `niftiVolumeToItkImage`.
 */
export function niftiVolumeToItkLabelImage(vol: NiftiVolume, name = "image"): Image {
  const [nx, ny, nz] = vol.dims;
  const { spacing, direction, origin } = geometry(vol);

  const img = new Image(new ImageType(3, IntTypes.UInt8, PixelTypes.Scalar, 1));
  img.name = name;
  img.origin = origin;
  img.spacing = spacing;
  img.direction = direction;
  img.size = [nx, ny, nz];
  const data = new Uint8Array(nx * ny * nz);
  for (let i = 0; i < data.length; i++) data[i] = Math.round(vol.voxels[i]);
  img.data = data;
  return img;
}

/** Inverse of `niftiVolumeToItkImage`. Converts whatever numeric component
 *  type itk-wasm returned (the pipelines above are float32-in/float32-out
 *  for intensity images, uint8 for label images) into this app's float32
 *  `NiftiVolume` convention -- same choice `lib/export.ts`'s `maskToVol`
 *  already makes for mask volumes. */
export function itkImageToNiftiVolume(img: Image): NiftiVolume {
  const [nx, ny, nz] = img.size;
  const spacing = img.spacing;
  const direction = img.direction as unknown as ArrayLike<number>;
  const affine = new Float32Array(16);
  for (let col = 0; col < 3; col++) {
    for (let row = 0; row < 3; row++) {
      affine[row * 4 + col] = direction[row * 3 + col] * spacing[col];
    }
  }
  affine[0 * 4 + 3] = img.origin[0];
  affine[1 * 4 + 3] = img.origin[1];
  affine[2 * 4 + 3] = img.origin[2];
  affine[3 * 4 + 3] = 1;

  const src = img.data;
  const voxels = new Float32Array(nx * ny * nz);
  if (src) for (let i = 0; i < voxels.length; i++) voxels[i] = Number(src[i]);

  return {
    dims: [nx, ny, nz],
    pixdim: [spacing[0], spacing[1], spacing[2]],
    affine,
    voxels,
    datatype: 16, // float32, same convention as maskToVol
  };
}
